import { round } from '@/utils/mathUtils';

import type { IRequestItem } from '../api/requestDialog.interface';

interface PriceCalculationInput {
  price: number;
  quantity: number;
  bonus: number;
  discount: number;
  specialDiscount: number;
  tax: number;
}

interface PriceCalculationResult {
  qty: number;
  totalQty: number;
  mainPrice: number;
  pharmacyDiscount: number;
  specialDiscount: number;
  totalTax: number;
  totalPrice: number;
  bonusValue: number;
  netPrice: number;
  netDiscount: number;
  avgPrice: number;
  TotalAfterDiscount: number;
  totalCustomerPrice: number;
}

/**
 * Calculate all pricing values based on input parameters
 *
 * @param input - All required inputs for price calculation
 * @returns All calculated price values
 */
export function calculatePricing(
  input: PriceCalculationInput,
): PriceCalculationResult {
  const { price, quantity, bonus, discount, specialDiscount, tax } = input;

  // Parse base quantity
  const qty = quantity === 0 ? 1 : quantity;

  // Calculate total quantity including bonus
  const totalQty = qty + bonus;

  // Calculate main price after tax deduction
  const mainPrice = price - tax;

  const totalCustomerPrice = price * qty;

  // Calculate discount factors
  const pharmacyDiscount = (100 - discount) / 100;
  const specialDiscountFactor = (100 - specialDiscount) / 100;

  // Calculate total tax amount
  const totalTax = tax ? totalQty * tax : 0;

  // Calculate total price before bonus value deduction
  const totalPrice =
    mainPrice * qty * pharmacyDiscount * specialDiscountFactor + totalTax;

  // Calculate the value of bonus items
  const bonusValue = bonus !== 0 ? (totalPrice / totalQty) * bonus : 0;

  // Calculate the net price per unit
  const netPrice = (totalPrice - bonusValue) / qty;

  // Calculate the net discount percentage
  const netDiscount =
    netPrice && price ? round((1 - netPrice / price || 0) * 100, 3) : 0;

  // Calculate the average price per item (including bonus)
  const avgPrice = totalQty > 0 ? totalPrice / totalQty : 0;

  const TotalAfterDiscount = totalPrice;

  return {
    qty,
    totalQty,
    mainPrice,
    pharmacyDiscount,
    specialDiscount: specialDiscountFactor,
    totalTax,
    totalPrice,
    bonusValue,
    netPrice,
    netDiscount,
    avgPrice,
    TotalAfterDiscount,
    totalCustomerPrice,
  };
}

/**
 * Calculate pricing for a request item
 */
export function calculateItemPricing(item: IRequestItem) {
  // Extract values from item with fallbacks to 0
  const price = item.Price || 0;
  const quantity = item.Qty || 0;
  const bonus = item.Bonus || 0;
  const discount = item.Discount || 0;
  const specialDiscount = item.DiscountPro || 0;
  const tax = item.Tax || 0;

  return calculatePricing({
    price,
    quantity,
    bonus,
    discount,
    specialDiscount,
    tax,
  });
}
